import React, { useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Building2 } from "lucide-react";
import {
  inputCls,
  PROPERTY_TYPES,
  PROPERTY_USAGE,
  PROPERTY_CONDITION,
  fmtNumber,
} from "./shared";
import Field from "./Field";
import DocumentList from "./DocumentList";

const empty = {
  land_id: "",
  name: "",
  property_type: "Building",
  built_up_area: 0,
  carpet_area: 0,
  floors_count: 0,
  construction_year: "",
  usage: "Academic",
  condition: "Good",
  documents: [],
  notes: "",
  is_active: true,
};

export default function PropertiesTab({ properties, lands, refresh, writable }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [filterLand, setFilterLand] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const filtered = filterLand
    ? properties.filter((p) => p.land_id === filterLand)
    : properties;
  const landById = useMemo(
    () => Object.fromEntries(lands.map((l) => [l.id, l])),
    [lands],
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty, land_id: filterLand || (lands[0]?.id ?? "") });
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      ...empty,
      ...p,
      construction_year: p.construction_year || "",
      documents: (p.documents || []).map((d) => ({ ...d })),
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Property name is required");
      return;
    }
    if (!form.land_id) {
      toast.error("Pick a parent Land");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        built_up_area: Number(form.built_up_area) || 0,
        carpet_area: Number(form.carpet_area) || 0,
        floors_count: Number(form.floors_count) || 0,
        construction_year: form.construction_year
          ? Number(form.construction_year)
          : null,
      };
      if (editing) {
        await api.put(`/properties/${editing.id}`, payload);
        toast.success("Property updated");
      } else {
        await api.post(`/properties`, payload);
        toast.success("Property added");
      }
      setOpen(false);
      refresh();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Save failed",
      );
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const p = confirmDelete;
    setConfirmDelete(null);
    try {
      await api.delete(`/properties/${p.id}`);
      toast.success("Property removed");
      refresh();
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed",
      );
    }
  };

  return (
    <div data-testid="properties-tab">
      <div className="flex justify-between items-end mb-4 gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
            Filter Land
          </span>
          <select
            data-testid="property-filter-land"
            className={inputCls + " w-72"}
            value={filterLand}
            onChange={(e) => setFilterLand(e.target.value)}
          >
            <option value="">All Lands</option>
            {lands.map((l) => (
              <option key={l.id} value={l.id}>
                {l.name}
              </option>
            ))}
          </select>
        </div>
        {writable && (
          <button
            onClick={openCreate}
            disabled={lands.length === 0}
            title={lands.length === 0 ? "Add a land record first" : undefined}
            data-testid="property-add-button"
            className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-40"
          >
            <Plus size={14} strokeWidth={1.5} /> Add Property
          </button>
        )}
      </div>
      {filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-12 text-center">
          <Building2
            size={28}
            strokeWidth={1.5}
            className="mx-auto text-[#234333]"
          />
          <div className="mt-3 font-serif text-2xl text-[#1c1a17]">
            No properties yet
          </div>
          <div className="mt-2 text-sm text-[#6b655b]">
            {lands.length === 0
              ? "Add a land record first, then add buildings or properties on it."
              : "Add the first building or property under a land record."}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="properties-table">
            <thead>
              <tr className="bg-[#f9f8f6] text-left">
                {[
                  "Name",
                  "Land",
                  "Type",
                  "Built-up",
                  "Floors",
                  "Usage",
                  "Condition",
                  "",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`property-row-${p.id}`}
                  className="border-t border-[#e5e0d8] hover:bg-[#f9f8f6]/60"
                >
                  <td className="px-4 py-3 text-[#1c1a17]">{p.name}</td>
                  <td className="px-4 py-3 text-[#6b655b]">
                    {landById[p.land_id]?.name || "—"}
                  </td>
                  <td className="px-4 py-3 text-[#6b655b]">
                    {p.property_type || "—"}
                  </td>
                  <td className="px-4 py-3 text-[#6b655b]">
                    {fmtNumber(p.built_up_area)} sqft
                  </td>
                  <td className="px-4 py-3 text-[#6b655b]">{p.floors_count || 0}</td>
                  <td className="px-4 py-3 text-[#6b655b]">{p.usage || "—"}</td>
                  <td className="px-4 py-3 text-[#6b655b]">{p.condition || "—"}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => openEdit(p)}
                      data-testid={`property-edit-${p.id}`}
                      className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#efece6] px-2 py-1 rounded-sm"
                    >
                      <Pencil size={14} strokeWidth={1.5} />{" "}
                      {writable ? "Edit" : "View"}
                    </button>
                    {writable && (
                      <button
                        onClick={() => setConfirmDelete(p)}
                        data-testid={`property-delete-${p.id}`}
                        className="ml-2 inline-flex items-center gap-1 text-[#9e2a2b] hover:bg-[#9e2a2b]/5 px-2 py-1 rounded-sm"
                      >
                        <Trash2 size={14} strokeWidth={1.5} /> Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-white border-[#e5e0d8] rounded-sm max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="property-dialog"
        >
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#1c1a17]">
              {editing ? "Edit Property" : "Add Property"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 py-2">
            <Field label="Parent Land *" full>
              <select
                data-testid="property-land-select"
                className={inputCls}
                value={form.land_id}
                disabled={!writable}
                onChange={(e) => set("land_id", e.target.value)}
              >
                <option value="">— Select Land —</option>
                {lands.map((l) => (
                  <option key={l.id} value={l.id}>
                    {l.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Property Name *">
              <input
                data-testid="property-name-input"
                className={inputCls}
                value={form.name}
                disabled={!writable}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>
            <Field label="Property Type">
              <select
                data-testid="property-type-select"
                className={inputCls}
                value={form.property_type}
                disabled={!writable}
                onChange={(e) => set("property_type", e.target.value)}
              >
                {PROPERTY_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Built-up Area (sqft)">
              <input
                type="number"
                className={inputCls}
                value={form.built_up_area}
                disabled={!writable}
                onChange={(e) => set("built_up_area", e.target.value)}
              />
            </Field>
            <Field label="Carpet Area (sqft)">
              <input
                type="number"
                className={inputCls}
                value={form.carpet_area}
                disabled={!writable}
                onChange={(e) => set("carpet_area", e.target.value)}
              />
            </Field>
            <Field label="Floors Count">
              <input
                type="number"
                className={inputCls}
                value={form.floors_count}
                disabled={!writable}
                onChange={(e) => set("floors_count", e.target.value)}
              />
            </Field>
            <Field label="Construction Year">
              <input
                type="number"
                className={inputCls}
                value={form.construction_year}
                disabled={!writable}
                onChange={(e) => set("construction_year", e.target.value)}
              />
            </Field>
            <Field label="Usage">
              <select
                data-testid="property-usage-select"
                className={inputCls}
                value={form.usage}
                disabled={!writable}
                onChange={(e) => set("usage", e.target.value)}
              >
                {PROPERTY_USAGE.map((u) => (
                  <option key={u} value={u}>
                    {u}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Condition">
              <select
                data-testid="property-condition-select"
                className={inputCls}
                value={form.condition}
                disabled={!writable}
                onChange={(e) => set("condition", e.target.value)}
              >
                {PROPERTY_CONDITION.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Notes" full>
              <textarea
                className={inputCls}
                rows={2}
                value={form.notes}
                disabled={!writable}
                onChange={(e) => set("notes", e.target.value)}
              />
            </Field>
            <div className="md:col-span-2">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-3">
                Property Documents
              </div>
              <DocumentList
                docs={form.documents}
                onChange={(docs) => set("documents", docs)}
                writable={writable}
              />
            </div>
          </div>
          <DialogFooter className="pt-4">
            <button
              onClick={() => setOpen(false)}
              className="bg-white hover:bg-[#f9f8f6] text-[#1c1a17] border border-[#e5e0d8] text-sm px-5 py-2 rounded-sm"
            >
              Cancel
            </button>
            {writable && (
              <button
                onClick={save}
                disabled={saving}
                data-testid="property-save"
                className="bg-[#234333] hover:bg-[#1b3326] text-white text-sm px-5 py-2 rounded-sm disabled:opacity-60"
              >
                {saving ? "Saving…" : editing ? "Update" : "Create"}
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={!!confirmDelete}
        onOpenChange={(v) => !v && setConfirmDelete(null)}
      >
        <AlertDialogContent className="bg-white border-[#e5e0d8] rounded-sm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-[#1c1a17]">
              Remove this property?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#6b655b]">
              {confirmDelete?.name} will be removed. This is blocked if it has units or
              active allocations.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white text-[#1c1a17] border-[#e5e0d8] rounded-sm">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-[#9e2a2b] hover:bg-[#7d2122] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
